//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

import { ColumnModel } from "../api/api.models"

import { Enums } from "../../models/common/enums"
import { OpActor, OpActorGroup, OpLocation } from "../../models/core/"
import { GridService } from "../../services/grid"
import { DataProvider } from '../../helpers/data-provider'
import { LocalizationHelper } from '../../helpers/localization-helper'
import { ToastHelper } from '../../helpers/toast-helper'
import { LogHelper } from '../../helpers/log-helper'
import { ConvertHelper } from '../../helpers/convert-helper'
import { CommonHelper } from '../../helpers/common-helper'


export class ActorHelper {

    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'actors' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdActor }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Actor }
    private static get LOG_PREFIX(): string { return '[SIMAX.Module.Actors]' }
    private static get SYSTEM_OWNER_ADDRESS_BOOK(): string { return 'System Owner Address Book' }

    // #endregion


    // #region Methods

    public static deleteActor(dataProvider: DataProvider, idActor: number, moduleName: Enums.SimaxModuleName): JQueryPromise<any> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        const toDelete: OpActor = new OpActor(null)

        toDelete.idActor = idActor

        dataProvider.Actor
            .deleteActor(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, moduleName))
                deferred.resolve()
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, moduleName))
                LogHelper.warn(ActorHelper.LOG_PREFIX, error)

                deferred.reject(errorMessage)
            })

        return deferred.promise()
    }

    public static getActorGroupsGridColumns(): ColumnModel[] {
        const nameColumn = new ColumnModel()
        const distributorColumn = new ColumnModel()
        const builtinColumn = new ColumnModel()

        nameColumn.idName = 'name'
        nameColumn.name = 'Name'
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)

        distributorColumn.idName = 'distributor'
        distributorColumn.name = 'Distributor'
        distributorColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Distributor)

        builtinColumn.idName = 'builtIn'
        builtinColumn.name = 'BuiltIn'
        builtinColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BuiltIn)
        builtinColumn.type = 7;
        builtinColumn['temporaryIdDataField'] = 'builtIn' //Tymczasowe rozwiazanie uzyte w grid-edit.controller.ts

        return [nameColumn, distributorColumn, builtinColumn]
    }

    public static saveActor(dataProvider: DataProvider, actor: OpActor, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpActor> {
        const deferred: JQueryDeferred<OpActor> = $.Deferred<OpActor>()

        dataProvider.Actor
            .saveActor(actor)
            .then((savedObject: OpActor) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.actorSaved, true, 0)
                }

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)

                if (!isBulkSave) {
                    ToastHelper.showError(errorMessage)
                    LogHelper.warn(ActorHelper.LOG_PREFIX, `Error during save actor.`, error)
                }

                deferred.reject(errorMessage)
            })

        return deferred.promise()
    }

    public static saveActorGroups(dataProvider: DataProvider, moduleName: Enums.SimaxModuleName, actor: OpActor, added: any[], removed: OpActorGroup[]): JQueryPromise<OpActor> {
        const newGroups: OpActorGroup[] = added.map(newGroupData => {
            const actorGroup: OpActorGroup = new OpActorGroup(null)

            actorGroup.idActorGroup = newGroupData.id
            actorGroup.opState = Enums.OpChangeState.New

            return actorGroup
        })

        const removedGroups: OpActorGroup[] = removed.map(removedGroupData => {
            const actorGroup: OpActorGroup = new OpActorGroup(null)

            actorGroup.idActorGroup = removedGroupData.idActorGroup
            actorGroup.opState = Enums.OpChangeState.Delete

            return actorGroup
        })

        const removedGroupIds: number[] = removedGroups.map(removedGroup => removedGroup.idActorGroup)

        const loadedGroups: OpActorGroup[] = actor.currentGroups
            .filter((loadedGroup: OpActorGroup) => removedGroupIds.indexOf(loadedGroup.idActorGroup) == -1)
            .map((loadedGroup: OpActorGroup) => {
                const actorGroup: OpActorGroup = new OpActorGroup(null)

                actorGroup.idActorGroup = loadedGroup.idActorGroup
                actorGroup.opState = Enums.OpChangeState.Loaded

                return actorGroup
            })

        const objectToSave: OpActor = new OpActor(null)

        objectToSave.idActor = actor.idActor
        objectToSave.opState = Enums.OpChangeState.Modified
        objectToSave.currentGroups = [].concat(...loadedGroups, ...newGroups, ...removedGroups);

        return ActorHelper.saveActor(dataProvider, objectToSave, moduleName);
    }

    public static saveActorLocations(dataProvider: DataProvider, moduleName: Enums.SimaxModuleName, actor: OpActor, added: any[], removed: OpActorGroup[]): JQueryPromise<OpActor> {
        const newLocations = added.map((newLocationData: any) => {
            const location: OpLocation = new OpLocation(null)

            location.idLocation = newLocationData.id
            location.opState = Enums.OpChangeState.New

            return location
        })

        const removedLocations = removed.map((removedLocationData: any) => {
            const location: OpLocation = new OpLocation(null)

            location.idLocation = removedLocationData.id
            location.opState = Enums.OpChangeState.Delete

            return location
        })

        const removedLocationIds: number[] = removedLocations.map((removedLocation: OpLocation) => removedLocation.idLocation)

        const loadedLocations: OpLocation[] = actor.currentLocations
            .filter((loadedLocation: OpLocation) => removedLocationIds.indexOf(loadedLocation.idLocation) == -1)
            .map((loadedLocation: OpLocation) => {
                const location = new OpLocation(null)

                location.idLocation = loadedLocation.idLocation
                location.opState = Enums.OpChangeState.Loaded

                return location
            })

        const objectToSave: OpActor = new OpActor(null)

        objectToSave.idActor = actor.idActor
        objectToSave.opState = Enums.OpChangeState.Modified
        objectToSave.currentLocations = [].concat(...loadedLocations, ...newLocations, ...removedLocations);

        return ActorHelper.saveActor(dataProvider, objectToSave, moduleName);
    }

    public static handleSystemOwnerAddressBook(removedGroups: OpActorGroup[]): OpActorGroup[] {
        const filteredGroups: OpActorGroup[] = _.filter(removedGroups, (group: OpActorGroup) => { return group.idActorGroup !== 1 && group.name !== ActorHelper.SYSTEM_OWNER_ADDRESS_BOOK })
        if (filteredGroups.length != removedGroups.length) {
            ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CantDeleteSystemAddressBook))
        }
        return filteredGroups
    }

    public static extendToGridObject(group: OpActorGroup, gridService: GridService): OpActorGroup {
        return _.extend(group, gridService.gridObjectFromIReferenceType(group))
    }

    // #endregion
} 